import { useCallback } from "react";
import SlotsDetailFilterContainer from "./filter/SlotsDetailFilterContainer";
import SlotsTable from "./table/SlotsDetailTableContainer";
import "../slots.scss";
import { Button } from "@pickit/pickit-components";
import SlotsDetailModalContainer from "./slotsDetailModal/SlotsDetailModalContainer";
import { SlotsDetailPropsType } from "./types";
import SlotCreationModal from "./slotCreationModal/SlotCreationModalContainer";
import "./i18n";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DATE_FORMATS } from "utils/constants";
import { formatHHMM } from "utils/date";
import MultiErrorAlert from "component/multiErrorAlert/MultiErrorAlert";

const SlotsDetail: React.FC<SlotsDetailPropsType> = (props) => {
  const { t } = useTranslation();
  const { testId, filters, exportSlots } = props;

  const renderExportButton = useCallback(() => {
    return <Button hierarchy="secondary" size="medium" label={t("global:export")} onClick={() => exportSlots(filters)} testId={testId} iconName="download" />;
  }, [exportSlots, filters, t, testId]);

  const renderSlotsErrorMessage = useCallback(() => {
    const slots = props.slotsWithError?.map((slot, index) => {
      const date = moment(slot.startAt).format(DATE_FORMATS.extraShortDate);
      const start = formatHHMM(slot.startAt);
      const finish = formatHHMM(slot.finishAt);
      return (
        <p
          key={index}
          className="slot-error"
          data-testId={`${testId}-error-modal-slot-${index + 1}`}
        >{`${slot.region.name} - ${date} - ${start} a ${finish} hs.`}</p>
      );
    });

    return (
      <>
        <span>{t("slots:notDeletedSlotsMessage")}</span>
        {slots}
      </>
    );
  }, [props.slotsWithError, t, testId]);

  return (
    <>
      <SlotsDetailFilterContainer
        regions={props.regions}
        status={props.status}
        testId={`${testId}-filter`}
      />
      <div className="slots-buttons-container">
        <div className="slots-buttons-left">
          <Button
            onClick={() => props.toggleCreationModalVisibility(true)}
            testId={`${testId}-add-slot-button`}
            label={t("slots:createSlot")}
            iconName="pencil"
            hierarchy="secondary"
            size="medium"
            width="220"
          />
          <Button
            disabled={!props.anySlotSelected}
            onClick={() => props.handleBulkDelete()}
            testId={`${testId}-delete-slot-button`}
            label={t("global:delete")}
            size="medium"
            hierarchy="primary"
            width="220"
          />
        </div>
        <div className="slots-buttons-right">
          {renderExportButton()}
        </div>
      </div>
      <SlotsTable
        openModal={() => props.toggleDetailModalVisibility(true)}
        testId={`${testId}`}
      />
      <MultiErrorAlert
        title={t("slots:notDeletedSlotsTitle")}
        isOpen={props.slotsWithError?.length > 0}
        onClose={props.clearSlotsWithError}
        testId={`${testId}-error-modal`}
      >
        {renderSlotsErrorMessage()}
      </MultiErrorAlert>
      <SlotsDetailModalContainer
        isOpen={props.showDetailModal}
        onClose={() => {
          props.toggleDetailModalVisibility(false);
          props.onRefresh();
        }}
        testId={`${testId}-modal`}
      />
      <SlotCreationModal
        isOpen={props.showCreationModal}
        onClose={() => props.toggleCreationModalVisibility(false)}
        regions={props.regions}
        testId={`${testId}-creation`}
      />
    </>
  );
};

export default SlotsDetail;
